import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { AronChatView } from "../AronChatView";
import { useAronChat } from "../../hooks/useAronChat";
import { useAronTabSignals } from "../../hooks/useAronTabSignals";
import { useAronUiStore } from "../../store/aronUiStore";

/**
 * One open tab. Every tab stays MOUNTED — hidden, not destroyed — exactly like
 * an open editor: a turn started in one tab keeps streaming (and keeps
 * signalling) while the user works in another.
 *
 * Nie zna workbencha: dostaje rozmowę i sposób podpięcia nowo utworzonej, więc
 * ten sam panel obsługuje karty na stronie i karty w pływającym oknie.
 */
export function AronTabPane({
  convId,
  active,
  onCreated,
  showDetach = true,
}: {
  convId: string | null;
  active: boolean;
  /** Pierwsza wiadomość świeżej karty tworzy rozmowę — tu wraca jej id. */
  onCreated: (convId: string) => void;
  /** „Wydziel do panelu" ma sens na stronie; w pływaku byłoby kręceniem się w kółko. */
  showDetach?: boolean;
}) {
  const navigate = useNavigate();
  const openDock = useAronUiStore((s) => s.openDock);
  const wrapRef = useRef<HTMLDivElement>(null);

  const { sending, handleSend, handleStop } = useAronChat(convId, (id) => {
    if (id) onCreated(id);
  });
  useAronTabSignals(convId, active);

  // A hidden pane cannot scroll, so a tab that received messages in the
  // background would come back showing the middle of the thread.
  useEffect(() => {
    if (!active) return;
    const el = wrapRef.current?.querySelector<HTMLElement>("[data-aron-scroll]");
    if (el) el.scrollTop = el.scrollHeight;
  }, [active]);

  const detach = () => {
    if (!convId) return;
    openDock(convId);
    navigate("/");
  };

  return (
    <div
      ref={wrapRef}
      className={active ? "flex min-h-0 min-w-0 flex-1 flex-col" : "hidden"}
      aria-hidden={!active}
    >
      <AronChatView
        // The pane header's actions live in the tab bar's "…" menu here — a
        // split pane cannot spare the row.
        showHeader={false}
        conversationId={convId}
        sending={sending}
        onSend={handleSend}
        onStop={handleStop}
        onDetach={showDetach && convId ? detach : undefined}
      />
    </div>
  );
}
